import { useCallback, useEffect, useReducer, useState } from "react";
import { useApiCall } from "@porchpass/ui";
import { useApiErrorHandler } from "./useApiErrorHandler";

export type ValueOrEvent =
  | React.ChangeEvent<HTMLInputElement>
  | string
  | number
  | boolean
  | object
  | undefined;

const stripIfEvent = (item: ValueOrEvent) =>
  typeof item === "object" && item !== null && "target" in item
    ? item.target.value
    : item;

export type FieldUpdaterType = (value: ValueOrEvent) => void;
export type FieldUpdateForDataType<T> = (field: keyof T) => FieldUpdaterType;

export function useFormLoad<TFormData extends Record<string, unknown>>(
  initialData: TFormData,
  loadFn: () => Promise<unknown>,
  mapResponse: (data: unknown) => Partial<TFormData>,
  options?: {
    enabled?: boolean;
    onError?: (error: Error) => void;
  },
) {
  const [formData, setFormData] = useState<TFormData>(initialData);
  const [reloadToken, reloadFormData] = useReducer((x) => !x, false);
  // `loading` is false on the first render, before the request starts, so it
  // can't tell "not loaded yet" from "loaded". This turns true once the first
  // load has settled (with data or an error).
  const [hasLoaded, setHasLoaded] = useState(false);
  const { handleApiError } = useApiErrorHandler();

  const loadApi = useApiCall(
    async () => {
      const data = await loadFn();
      return mapResponse(data);
    },
    {
      toast: { success: false, error: false },
      onError: (error) => {
        handleApiError(error);
        options?.onError?.(error);
      },
    },
  );

  useEffect(() => {
    if (options?.enabled === false) {
      return;
    }
    void loadApi.execute().then((data) => {
      if (data) {
        setFormData((prev) => ({ ...prev, ...data }));
      }
      setHasLoaded(true);
    });
  }, [options?.enabled, reloadToken]);

  const updateField: FieldUpdateForDataType<TFormData> = useCallback(
    (field) => (value) => {
      setFormData((prev) => ({
        ...prev,
        [field]: stripIfEvent(value),
      }));
    },
    [],
  );

  return {
    formData,
    setFormData,
    updateField,
    loading: loadApi.isLoading,
    hasLoaded,
    reloadFormData,
  };
}
